import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  LinkOutlined,
  SwapOutlined,
  UpOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import {
  anularMatch,
  getReporteDetalle,
  getReporteDownload,
  getReporteDownloadFiltradoStatus,
  iniciarReporteDownloadFiltrado,
} from '../../api';
import type { DetalleFiltros, DetalleItem, ResumenCuenta, ResumenMoneda } from '../../api';
import { isAdmin, isStaff, useAuth } from '../../auth';
import { EmptyState, Loader } from '../../components';
import { cleanEmpresa, formatMXN, formatMonto, scoreColor } from '../../utils/format';
import { usePersistedState } from '../../utils/usePersistedState';
import { CATEGORIAS_SIN_CFDI, categoriaSinCfdiMeta } from '../../utils/categoriaSinCfdi';
import { useFiltroFecha } from '../../filtroFecha';
import { useEmpresa } from '../../empresa';

const { Title, Paragraph, Text } = Typography;

const STATUS_META: Record<string, { label: string; bg: string; color: string }> = {
  MATCHED:               { label: 'Conciliado',     bg: '#d1e7dd', color: '#0a3622' },
  MATCHED_IA:            { label: 'Conciliado',     bg: '#d1e7dd', color: '#0a3622' },
  SIN_CFDI:              { label: 'Sin CFDI',       bg: '#fff3cd', color: '#664d03' },
  SIN_MOVIMIENTO_BANCO:  { label: 'Sin Movimiento', bg: '#ffe5d0', color: '#7c3e0a' },
  MATCHED_TRASPASO_INTERNO: { label: 'Conciliado',     bg: '#d1e7dd', color: '#0a3622' },
  CONCILIADO_SIN_CFDI:   { label: 'Conciliado',     bg: '#d1e7dd', color: '#0a3622' },
  PAGADA_VIA_COMPLEMENTO: { label: 'PPD Complemento', bg: '#e2e3e5', color: '#41464b' },
};

const PAGE_SIZE_DEFAULT = 50;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const MONTO_DEBOUNCE_MS = 400;

/** Moneda "efectiva" de un item: la del CFDI (fuente SAT) si matcheó, si no la
 * declarada del extracto de origen del movimiento, si no hay ninguna, MXN. */
function monedaEfectiva(item: DetalleItem): string {
  return item.cfdi?.moneda || item.banco?.moneda || 'MXN';
}

/** value inequívoco para el <Select> de tipo/método — '::' no puede aparecer
 * en tipo_doc/metodo_pago reales, a diferencia del '—' usado para mostrar
 * metodo_pago nulo, que sí podría confundirse si se usara como separador. */
function tipoMetodoValue(tipoDoc: string | null, metodoPago: string | null | undefined): string {
  return `${tipoDoc ?? ''}::${metodoPago ?? ''}`;
}

/** Monedas con movimientos bancarios reales; las facturas sin movimiento no cuentan. */
function monedasConMovimientoBanco(
  porCuenta: Record<string, ResumenCuenta> | null | undefined,
  porMoneda: Record<string, ResumenMoneda> | null | undefined,
  fallback: string[],
): string[] {
  const cuentas = Object.values(porCuenta ?? {});
  if (cuentas.length > 0) {
    return [...new Set(cuentas.filter(c => (c.total_movimientos ?? 0) > 0).map(c => c.moneda))].sort();
  }
  const monedasResumen = Object.entries(porMoneda ?? {});
  if (monedasResumen.length > 0) {
    return monedasResumen.filter(([, m]) => (m.total_movimientos_banco ?? 0) > 0).map(([mon]) => mon).sort();
  }
  return fallback;
}

function tipoMetodoLabel(tipoDoc: string | null, metodoPago: string | null | undefined): string {
  return `${tipoDoc ?? ''} / ${metodoPago || '—'}`;
}

export function ReporteDetalle() {
  const { message, notification } = App.useApp();
  const { rfc = '' } = useParams<{ rfc: string }>();
  const [searchParams] = useSearchParams();
  const conciliacionId = searchParams.get('id') || undefined;
  const navigate = useNavigate();
  const { user } = useAuth();
  const staff = isStaff(user);

  // Vuelve al listado si cambia el selector global de empresa (no en el mount).
  const { rfc: rfcSeleccionMenu } = useEmpresa();
  const rfcSeleccionMenuRef = useRef(rfcSeleccionMenu);
  useEffect(() => {
    if (rfcSeleccionMenuRef.current === rfcSeleccionMenu) return;
    rfcSeleccionMenuRef.current = rfcSeleccionMenu;
    navigate(staff ? '/reportes' : '/mis-conciliaciones');
  }, [rfcSeleccionMenu, navigate, staff]);
  // Filtro de fecha mes único -> rango, compartido entre módulos (Context
  // montado en AppLayout, ver src/filtroFecha/) para que la selección
  // persista al navegar.
  const { rango, setRango } = useFiltroFecha();

  const [reporte, setReporte] = useState<Awaited<ReturnType<typeof getReporteDetalle>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [sinConciliaciones, setSinConciliaciones] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [anularTarget, setAnularTarget] = useState<DetalleItem | null>(null);
  const [motivoAnular, setMotivoAnular] = useState('');
  const [anulando, setAnulando] = useState(false);

  const [activeTab, setActiveTab] = usePersistedState<'resumen_cuenta' | 'sin_movimiento'>('reporteDetalle.tab', 'resumen_cuenta');
  // Filtros particulares persistidos en localStorage por pantalla (ver
  // usePersistedState), no solo el rango de fecha global (useFiltroFecha).
  const [filterStatus, setFilterStatus] = usePersistedState('reporteDetalle.status', 'TODOS');
  // Selector real de número de cuenta (filtra el "Resumen de Cuenta" a una
  // cuenta específica, no solo la muestra como columna informativa).
  const [filterCuenta, setFilterCuenta] = usePersistedState('reporteDetalle.cuenta', 'TODOS');
  const [filterMoneda, setFilterMoneda] = usePersistedState('reporteDetalle.moneda', 'TODOS');
  const [filterTipoMetodo, setFilterTipoMetodo] = usePersistedState('reporteDetalle.tipoMetodo', 'TODOS');
  const [montoMin, setMontoMin]         = usePersistedState<number | null>('reporteDetalle.montoMin', null);
  const [montoMax, setMontoMax]         = usePersistedState<number | null>('reporteDetalle.montoMax', null);
  const [montoMinDeb, setMontoMinDeb]   = useState<number | null>(null);
  const [montoMaxDeb, setMontoMaxDeb]   = useState<number | null>(null);
  const [filterUuid, setFilterUuid]     = usePersistedState('reporteDetalle.uuid', '');
  const [filterRfc, setFilterRfc]       = usePersistedState('reporteDetalle.rfc', '');
  const [resumenMonedaColapsado, setResumenMonedaColapsado] = usePersistedState('reporteDetalle.resumenMonedaColapsado', true);
  const [resumenCuentaColapsado, setResumenCuentaColapsado] = usePersistedState('reporteDetalle.resumenCuentaColapsado', true);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  // Resetea los filtros locales (persistidos con key global) al cambiar de
  // reporte, sin tocar el mount inicial ni el rango de fecha global.
  const rfcConciliacionKey = `${rfc}::${conciliacionId ?? ''}`;
  const prevRfcConciliacionKeyRef = useRef(rfcConciliacionKey);
  useEffect(() => {
    if (prevRfcConciliacionKeyRef.current === rfcConciliacionKey) return;
    prevRfcConciliacionKeyRef.current = rfcConciliacionKey;
    setFilterStatus('TODOS');
    setFilterCuenta('TODOS');
    setFilterMoneda('TODOS');
    setFilterTipoMetodo('TODOS');
    setMontoMin(null);
    setMontoMax(null);
    setFilterUuid('');
    setFilterRfc('');
  }, [rfcConciliacionKey, setFilterStatus, setFilterCuenta, setFilterMoneda,
      setFilterTipoMetodo, setMontoMin, setMontoMax, setFilterUuid, setFilterRfc]);

  // Debounce de los montos — evita un fetch por cada tecla en el InputNumber.
  useEffect(() => {
    const t = setTimeout(() => setMontoMinDeb(montoMin), MONTO_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [montoMin]);
  useEffect(() => {
    const t = setTimeout(() => setMontoMaxDeb(montoMax), MONTO_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [montoMax]);

  const [tipoDocSel, metodoPagoSel] = useMemo(() => {
    if (filterTipoMetodo === 'TODOS') return [undefined, undefined] as const;
    const [td, mp] = filterTipoMetodo.split('::');
    return [td || undefined, mp || undefined] as const;
  }, [filterTipoMetodo]);

  const desde = rango?.[0] ? rango[0].format('YYYY-MM-DD') : undefined;
  const hasta = rango?.[1] ? rango[1].format('YYYY-MM-DD') : undefined;

  // Filtros sin page/page_size — reusados por el fetch principal (+paginación)
  // y por la descarga de Excel filtrado (que trae TODO el conjunto, no una página).
  const filtrosBase: DetalleFiltros = useMemo(() => ({
    id:          conciliacionId,
    tab:         activeTab,
    status:      filterStatus,
    desde,
    hasta,
    numero_cuenta: activeTab === 'resumen_cuenta' && filterCuenta !== 'TODOS' ? filterCuenta : undefined,
    moneda:      filterMoneda,
    tipo_doc:    tipoDocSel,
    metodo_pago: metodoPagoSel,
    monto_min:   montoMinDeb ?? undefined,
    monto_max:   montoMaxDeb ?? undefined,
    uuid:        filterUuid.trim() || undefined,
    rfc:         filterRfc.trim() || undefined,
  }), [conciliacionId, activeTab, filterStatus, desde, hasta, filterCuenta, filterMoneda, tipoDocSel, metodoPagoSel, montoMinDeb, montoMaxDeb, filterUuid, filterRfc]);

  const filtrosKey = JSON.stringify(filtrosBase);
  const prevFiltrosKeyRef = useRef(filtrosKey);

  useEffect(() => {
    const filtrosCambiaron = prevFiltrosKeyRef.current !== filtrosKey;
    prevFiltrosKeyRef.current = filtrosKey;

    // Si cambió algún filtro (no la página) y no estábamos ya en la página 1,
    // resetear primero — este mismo effect se vuelve a disparar cuando
    // `page` cambie, ya con filtrosCambiaron=false, y ahí sí se hace el fetch.
    if (filtrosCambiaron && page !== 1) {
      setPage(1);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSinConciliaciones(false);
    getReporteDetalle(rfc, { ...filtrosBase, page, page_size: pageSize })
      .then(d => {
        if (cancelled) return;
        if (d.sin_conciliaciones) setSinConciliaciones(true);
        else setReporte(d);
      })
      .catch(() => { if (!cancelled) setError('No se Pudo Cargar el Detalle.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfc, filtrosKey, page, pageSize]);

  const monedas       = reporte?.filtros_disponibles?.monedas ?? [];
  const tiposMetodos = reporte?.filtros_disponibles?.tipos_metodo ?? [];
  // Cuentas distintas dentro de esta conciliación, para el selector real
  // (dropdown, no solo columna informativa). Solo se ofrecen cuando hay más
  // de una — con una sola cuenta no hay nada que elegir.
  const cuentas = reporte?.filtros_disponibles?.cuentas ?? [];

  const statusOptions = useMemo(() => {
    const base = [{ value: 'TODOS', label: 'Todos los Estados' }];
    if (activeTab === 'resumen_cuenta') {
      return [
        ...base,
        { value: 'MATCHED', label: 'Conciliado' },
        { value: 'CONCILIADO_SIN_CFDI', label: 'Préstamo / Inversión' },
        { value: 'SIN_CFDI', label: 'Sin CFDI' },
      ];
    }
    return [
      ...base,
      { value: 'SIN_MOVIMIENTO_BANCO', label: 'Sin Movimiento' },
      { value: 'PAGADA_VIA_COMPLEMENTO', label: 'PPD Complemento' },
    ];
  }, [activeTab]);

  // Filtros persistidos de otra conciliación que aquí no existen (o cuyo selector está oculto) se resetean.
  const facetas = reporte?.filtros_disponibles;
  useLayoutEffect(() => {
    if (!facetas) return;
    if (filterStatus !== 'TODOS' && !statusOptions.some(o => o.value === filterStatus)) setFilterStatus('TODOS');
    if (filterCuenta !== 'TODOS' && !(activeTab === 'resumen_cuenta' && facetas.cuentas.some(c => c.numero_cuenta === filterCuenta))) {
      setFilterCuenta('TODOS');
    }
    if (filterMoneda !== 'TODOS' && !(facetas.monedas.length > 1 && facetas.monedas.includes(filterMoneda))) setFilterMoneda('TODOS');
    if (filterTipoMetodo !== 'TODOS' && !facetas.tipos_metodo.some(tm => tipoMetodoValue(tm.tipo_doc, tm.metodo_pago) === filterTipoMetodo)) {
      setFilterTipoMetodo('TODOS');
    }
  }, [facetas, activeTab, statusOptions, filterStatus, filterCuenta, filterMoneda, filterTipoMetodo,
      setFilterStatus, setFilterCuenta, setFilterMoneda, setFilterTipoMetodo]);

  // Saldo corrido solo con movimientos bancarios de una sola moneda; un filtro de
  // moneda igual a esa no recorta filas del banco (mismo criterio que el backend).
  const monedasBanco = monedasConMovimientoBanco(reporte?.resumen_por_cuenta, reporte?.resumen_por_moneda, monedas);
  const cuentaSeleccionada = activeTab === 'resumen_cuenta' && filterCuenta !== 'TODOS';
  const monedasCuentaSel = cuentaSeleccionada
    ? [...new Set(Object.values(reporte?.resumen_por_cuenta ?? {}).filter(c => c.numero_cuenta === filterCuenta).map(c => c.moneda))]
    : [];
  const monedasScope = cuentaSeleccionada ? monedasCuentaSel : monedasBanco;
  const filtroMonedaNoRecorta = filterMoneda === 'TODOS' || (monedasScope.length === 1 && monedasScope[0] === filterMoneda);
  // Con cuenta elegida solo vale su saldo inicial propio, nunca el global (conciliación legacy).
  const saldoVisible = reporte?.saldo_anterior != null && filtroMonedaNoRecorta && (cuentaSeleccionada
    ? reporte?.saldo_anterior_por_cuenta === true && monedasCuentaSel.length <= 1
    : monedasBanco.length <= 1);
  const monedaSaldo = filterMoneda !== 'TODOS'
    ? filterMoneda
    : monedasCuentaSel.length === 1
      ? monedasCuentaSel[0]
      : monedasBanco.length === 1 ? monedasBanco[0] : undefined;

  const items = useMemo(() => reporte?.detalle.items ?? [], [reporte]);

  // Cuando "Resumen de Cuenta" mezcla más de una cuenta del mismo banco,
  // hay que dejar claro a qué cuenta pertenece cada fila — sin esto, dos
  // cuentas BBVA distintas se leerían como una sola secuencia. Los items ya
  // vienen ordenados por
  // (banco_fuente, numero_cuenta, orden) desde el backend. `cuentas` viene
  // de filtros_disponibles (global a la conciliación, no a la página
  // actual) — evita que la columna aparezca/desaparezca entre páginas si la
  // paginación separa las cuentas en páginas distintas.
  const mostrarColumnaCuenta = activeTab === 'resumen_cuenta' && cuentas.length > 1;

  const displayItems = useMemo(() => {
    if (!saldoVisible || page !== 1 || reporte?.saldo_anterior == null) return items;
    const saldoInicialRow: DetalleItem = {
      status: 'SALDO_INICIAL',
      match_score: null,
      banco: {
        id: 'saldo-inicial', fecha: null, banco_fuente: null, descripcion: 'SALDO INICIAL',
        deposito: null, retiro: null, monto: 0,
      },
      cfdi: null,
      sugerencias: [],
      saldo_corrido: reporte.saldo_anterior,
    };
    return [saldoInicialRow, ...items];
  }, [items, saldoVisible, page, reporte?.saldo_anterior]);

  function resetFiltrosLocales() {
    setFilterStatus('TODOS');
    setFilterCuenta('TODOS');
    setFilterMoneda('TODOS');
    setFilterTipoMetodo('TODOS');
    setMontoMin(null);
    setMontoMax(null);
    setFilterUuid('');
    setFilterRfc('');
  }

  function resetFiltros() {
    resetFiltrosLocales();
    setRango(null);
  }

  const hayFiltros = filterStatus !== 'TODOS' || !!desde || !!hasta ||
    filterCuenta !== 'TODOS' || filterMoneda !== 'TODOS' || filterTipoMetodo !== 'TODOS' ||
    montoMin != null || montoMax != null || !!filterUuid.trim() || !!filterRfc.trim();

  // El Excel filtrado (con columnas CFDI configurables, "Editar Columnas")
  // es async: re-parsear XML de S3 por cada CFDI puede superar los 29s que
  // tolera una request síncrona de API Gateway REST (límite duro de AWS).
  // iniciarReporteDownloadFiltrado dispara el job; acá hacemos polling hasta
  // que termine (ver conciliacion_get_download_filtrado_iniciar/status).
  async function esperarDescargaFiltrada(jobId: string): Promise<string | null> {
    const INTERVALO_MS = 3000;
    const MAX_INTENTOS = 40; // ~2 minutos
    for (let intento = 0; intento < MAX_INTENTOS; intento++) {
      await new Promise(resolve => setTimeout(resolve, INTERVALO_MS));
      const status = await getReporteDownloadFiltradoStatus(rfc, jobId);
      if (status.estado === 'COMPLETADO') return status.url ?? null;
      if (status.estado === 'ERROR') {
        message.error(status.mensaje || 'No se pudo generar el Excel.');
        return null;
      }
    }
    message.error('La generación del Excel está tardando más de lo esperado. Intenta de nuevo en un momento.');
    return null;
  }

  async function descargarExcel() {
    // La selección de columnas CFDI ("Editar Columnas") no viaja acá — el
    // backend la resuelve por el usuario autenticado (GET/PUT
    // /preferencias/columnas-excel), no por query param.
    setDownloadingXlsx(true);
    try {
      // Usa los mismos valores (debounced) que filtrosBase, no hayFiltros
      // (que lee montoMin/montoMax sin debounce) — si no, tipear un monto y
      // clickear Excel antes de los 400ms del debounce elegía el endpoint
      // filtrado pero mandaba un body sin el monto todavía aplicado: el
      // usuario recibía un Excel sin el filtro que acababa de poner.
      const hayFiltrosParaDescarga = filterStatus !== 'TODOS' || !!desde || !!hasta ||
        filterCuenta !== 'TODOS' || filterMoneda !== 'TODOS' ||
        filterTipoMetodo !== 'TODOS' || montoMinDeb != null || montoMaxDeb != null ||
        !!filterUuid.trim() || !!filterRfc.trim();
      // No window.open() directo en ninguna rama: en la filtrada pasan hasta
      // ~2 min de polling desde el click, y hasta el GET directo de la otra
      // rama alcanza para que Safari lo trate como popup no solicitado — en
      // ambos casos el navegador lo bloquea en silencio, sin error visible.
      // El botón de la notificación hace el open dentro de un click real.
      let url: string | null;
      if (hayFiltrosParaDescarga) {
        const { job_id } = await iniciarReporteDownloadFiltrado(rfc, filtrosBase);
        url = await esperarDescargaFiltrada(job_id);
      } else {
        url = (await getReporteDownload(rfc)).url;
      }
      if (url) {
        notification.success({
          message: 'Excel listo',
          description: 'El reporte de conciliación ya se generó.',
          btn: <Button type="primary" size="small" onClick={() => window.open(url, '_blank')}>Descargar</Button>,
          duration: 0,
        });
      }
    } catch {
      message.error('No se Pudo Descargar el Excel.');
    } finally {
      setDownloadingXlsx(false);
    }
  }

  async function confirmarAnular() {
    const resultadoId = anularTarget?.banco?.id;
    const motivo = motivoAnular.trim();
    if (!resultadoId || !motivo) return;
    setAnulando(true);
    try {
      await anularMatch(rfc, resultadoId, motivo);
    } catch {
      message.error('No se pudo anular el match.');
      setAnulando(false);
      return;
    }
    message.success(anularTarget?.status === 'MATCHED_TRASPASO_INTERNO'
      ? 'Traspaso deshecho — ambos movimientos vuelven a "Sin CFDI" y están disponibles otra vez en Conciliación Manual.'
      : anularTarget?.status === 'CONCILIADO_SIN_CFDI'
        ? 'Marca deshecha — el movimiento vuelve a "Sin CFDI" y está disponible otra vez en Conciliación Manual.'
        : 'Match anulado — el movimiento vuelve a "Sin CFDI" en Resumen de Cuenta y está disponible otra vez en Conciliación Manual.');
    setAnularTarget(null);
    setMotivoAnular('');
    // Re-fetch con los mismos filtros/página activos — la fila anulada
    // desaparece de "Conciliados" sin tener que recargar toda la pantalla.
    // El anular ya se hizo del lado del servidor: si este refetch falla, no
    // se debe mostrar un error que sugiera que la anulación no ocurrió.
    setLoading(true);
    try {
      const actualizado = await getReporteDetalle(rfc, { ...filtrosBase, page, page_size: pageSize });
      if (actualizado.sin_conciliaciones) setSinConciliaciones(true);
      else setReporte(actualizado);
    } catch {
      message.warning('Match anulado, pero no se pudo refrescar la lista — recarga la página.');
    } finally {
      setLoading(false);
      setAnulando(false);
    }
  }

  if (loading && !reporte) return <Loader tip="Cargando Detalle..." />;
  if (sinConciliaciones) {
    return (
      <Card>
        <EmptyState description={`${rfc} Todavía No Tiene Conciliaciones.`}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(staff ? '/reportes' : '/mis-conciliaciones')}>
              Volver
            </Button>
            {isAdmin(user) && (
              <Button type="primary" onClick={() => navigate('/carga')}>Cargar Extractos</Button>
            )}
          </Space>
        </EmptyState>
      </Card>
    );
  }
  if (error || !reporte) {
    return <Alert type="error" message={error || `No se Encontró el Reporte para ${rfc}`} showIcon />;
  }

  const r = reporte.resumen;
  const anulandoTraspaso = anularTarget?.status === 'MATCHED_TRASPASO_INTERNO';
  const anulandoSinCfdi = anularTarget?.status === 'CONCILIADO_SIN_CFDI'
    ? categoriaSinCfdiMeta(anularTarget.categoria_sin_cfdi)?.nombre ?? 'Conciliado sin CFDI'
    : null;
  const totalesFiltrado = reporte.detalle.totales_filtrados;
  // Las facturas pueden estar en otra moneda que el banco: solo se totaliza si hay una sola moneda que mostrar.
  const cfdiTotalPorMoneda = totalesFiltrado.cfdi_total_por_moneda ?? {};
  const monedasCfdi = Object.keys(cfdiTotalPorMoneda);
  const monedaCfdiTotal = filterMoneda !== 'TODOS' ? filterMoneda : monedasCfdi.length === 1 ? monedasCfdi[0] : undefined;
  const cfdiTotal = monedaCfdiTotal ? cfdiTotalPorMoneda[monedaCfdiTotal] : undefined;

  const columns: ColumnsType<DetalleItem> = [
    { title: 'Fecha Banco', key: 'fecha', width: 100, align: 'center', render: (_, i) => i.banco?.fecha || '—' },
    { title: 'Banco', key: 'banco', width: 90, align: 'center', render: (_, i) => i.banco?.banco_fuente ? <Tag>{i.banco.banco_fuente}</Tag> : '—' },
    ...(mostrarColumnaCuenta ? [{
      title: 'Cuenta', key: 'numero_cuenta', width: 110, align: 'center' as const,
      render: (_: unknown, i: DetalleItem) => i.banco?.numero_cuenta
        ? <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{i.banco.numero_cuenta}</Text>
        : '—',
    }] : []),
    ...(monedas.length > 1 ? [{
      title: 'Moneda', key: 'moneda', width: 80, align: 'center' as const,
      render: (_: unknown, i: DetalleItem) => i.status === 'SALDO_INICIAL' ? '—' : <Tag>{monedaEfectiva(i)}</Tag>,
    }] : []),
    {
      title: 'Concepto',
      key: 'concepto',
      width: 260,
      ellipsis: true,
      align: 'center',
      render: (_, i) => <span title={i.banco?.descripcion ?? undefined}>{i.banco?.descripcion || '—'}</span>,
    },
    {
      title: 'Depósito',
      key: 'deposito',
      width: 110,
      align: 'center',
      render: (_, i) => i.banco?.deposito != null ? formatMonto(i.banco.deposito, monedaEfectiva(i)) : '—',
    },
    {
      title: 'Retiro',
      key: 'retiro',
      width: 110,
      align: 'center',
      render: (_, i) => i.banco?.retiro != null ? formatMonto(i.banco.retiro, monedaEfectiva(i)) : '—',
    },
    {
      title: 'Saldo',
      key: 'saldo',
      width: 110,
      align: 'center',
      render: (_, i) => (saldoVisible && monedaSaldo && i.saldo_corrido != null && i.banco != null)
        ? <Text strong>{formatMonto(i.saldo_corrido, monedaSaldo)}</Text>
        : <Text type="secondary" title="El saldo corrido solo se muestra con movimientos bancarios de una sola moneda (sin filtro de moneda o filtrando por esa misma) y en filas con movimiento bancario. Al elegir una cuenta, esta necesita su propio saldo inicial (si falta, vuelve a conciliar).">—</Text>,
    },
    {
      title: 'UUID CFDI',
      key: 'uuid',
      width: 110,
      align: 'center',
      render: (_, i) => i.cfdi?.uuid
        ? (i.cfdi.ver_cfdi_url
            ? <a href={i.cfdi.ver_cfdi_url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'monospace', fontSize: 11 }} title={i.cfdi.uuid}>
                {i.cfdi.uuid.slice(0, 6)}…<LinkOutlined style={{ marginLeft: 4 }} />
              </a>
            : <Text style={{ fontFamily: 'monospace', fontSize: 11 }} title={i.cfdi.uuid}>{i.cfdi.uuid.slice(0, 6)}…</Text>)
        : '—',
    },
    {
      title: 'Dirección',
      key: 'direccion',
      width: 100,
      align: 'center',
      render: (_, i) => {
        if (!i.cfdi?.rfc_emisor) return '—';
        const esEmitido = i.cfdi.rfc_emisor === reporte.rfc;
        return <Tag color={esEmitido ? 'blue' : 'purple'}>{esEmitido ? 'Emitido' : 'Recibido'}</Tag>;
      },
    },
    { title: 'RFC Emisor', key: 'rfc_emisor', width: 110, align: 'center', render: (_, i) => <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{i.cfdi?.rfc_emisor || '—'}</Text> },
    {
      title: 'Total CFDI',
      key: 'cfdi_total',
      width: 110,
      align: 'center',
      render: (_, i) => i.cfdi?.total != null ? formatMonto(i.cfdi.total, monedaEfectiva(i)) : '—',
    },
    {
      title: 'Tipo / Método',
      key: 'tipo',
      width: 110,
      align: 'center',
      render: (_, i) => i.cfdi?.tipo_doc
        ? <Text style={{ fontSize: 11, textAlign: 'center', display: 'block' }}>{i.cfdi.tipo_doc} / {i.cfdi.metodo_pago || '—'}</Text>
        : <Text style={{ display: 'block', textAlign: 'center' }}>—</Text>,
    },
    {
      title: 'Score',
      key: 'match_score',
      width: 90,
      align: 'center',
      render: (_, i) => {
        if (i.status === 'MATCHED_TRASPASO_INTERNO') {
          return <Tag color="#198754" style={{ borderColor: '#198754' }}>TC</Tag>;
        }
        if (i.status === 'CONCILIADO_SIN_CFDI') {
          const cat = categoriaSinCfdiMeta(i.categoria_sin_cfdi);
          return <Tag color="#198754" style={{ borderColor: '#198754' }} title={cat?.nombre}>{cat?.codigo ?? '—'}</Tag>;
        }
        const esMatch = i.status === 'MATCHED' || i.status === 'MATCHED_IA';
        if (!esMatch) return '—';
        if (i.match_score == null) {
          return <Tag color="#198754" style={{ borderColor: '#198754' }}>MM</Tag>;
        }
        const score = Math.round(i.match_score);
        return <Tag color={scoreColor(score)} style={{ borderColor: scoreColor(score) }}>{score}%</Tag>;
      },
    },
    {
      title: 'Estado',
      key: 'status',
      width: 130,
      align: 'center',
      render: (_, i) => {
        if (i.status === 'SALDO_INICIAL') {
          return <Tag style={{ background: '#1a3a5c', color: '#FFFFFF', border: 'none', fontWeight: 700 }}>SALDO INICIAL</Tag>;
        }
        const meta = STATUS_META[i.status] || STATUS_META['SIN_CFDI'];
        return <Tag style={{ background: meta.bg, color: meta.color, border: 'none' }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 90,
      align: 'center',
      render: (_, i) => {
        const esTraspaso = i.status === 'MATCHED_TRASPASO_INTERNO';
        const esSinCfdi = i.status === 'CONCILIADO_SIN_CFDI';
        const esMatch = i.status === 'MATCHED' || i.status === 'MATCHED_IA' || esTraspaso || esSinCfdi;
        if (!esMatch || !i.banco?.id) return null;
        return (
          <Button
            danger
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            title={esTraspaso ? 'Deshacer traspaso' : esSinCfdi ? 'Deshacer conciliado sin CFDI' : 'Anular match'}
            onClick={() => { setAnularTarget(i); setMotivoAnular(''); }}
          />
        );
      },
    },
  ];
  // scroll.x = suma de anchos: con un x fijo menor, Concepto (ellipsis) colapsaba a 0px.
  const anchoTabla = columns.reduce((total, c) => total + (typeof c.width === 'number' ? c.width : 0), 0);

  return (
    <div>
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(staff ? '/reportes' : '/mis-conciliaciones')} style={{ paddingLeft: 0, marginBottom: 12 }}>
        Volver
      </Button>

      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }} gutter={16}>
        <Col flex="auto">
          <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
            <Tag style={{ fontFamily: 'monospace', fontSize: 14 }}>{reporte.rfc}</Tag>
          </Title>
          {reporte.nombre_empresa && (
            <Text strong style={{ color: '#495057' }}>{cleanEmpresa(reporte.nombre_empresa)}</Text>
          )}
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Período: {reporte.periodo?.inicio} — {reporte.periodo?.fin}
          </Paragraph>
        </Col>
        <Col>
          <Space>
            {staff && (
              <Button
                type="primary"
                icon={<SwapOutlined />}
                onClick={() => navigate(`/reportes/${rfc}/match${conciliacionId ? `?id=${conciliacionId}` : ''}`)}
                style={{ background: '#6f42c1', borderColor: '#6f42c1' }}
              >
                Conciliación Manual
              </Button>
            )}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloadingXlsx}
              onClick={descargarExcel}
              style={{ background: '#198754', borderColor: '#198754' }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <StatCard label="% Conciliado"          value={`${Math.round(r.porcentaje_conciliado ?? 0)}%`} />
        <StatCard label="Conciliados"           value={r.matched ?? 0} />
        <StatCard label="Traspasos"             value={r.traspasos_internos ?? 0} />
        <StatCard label="Préstamo / Inversión"  value={r.conciliados_sin_cfdi ?? 0} />
        <StatCard label="Sin CFDI"              value={r.sin_cfdi ?? 0} />
        <StatCard label="Sin Movimiento"        value={r.sin_movimiento_banco ?? 0} />
        <StatCard label="Total Facturas SAT"    value={(r.matched ?? 0) + (r.sin_movimiento_banco ?? 0)} />
        <StatCard label="Total Banco"           value={formatMXN(r.monto_total_banco)} />
        <StatCard label="Total SAT Conciliado"  value={formatMXN(r.monto_total_sat_matched ?? 0)} />
        <StatCard label="Diferencia"            value={formatMXN(r.diferencia)} />
      </Row>

      {reporte.resumen_por_moneda && Object.keys(reporte.resumen_por_moneda).length > 1 && (
        <Card
          size="small"
          title="Resumen por moneda"
          style={{ marginBottom: 20 }}
          styles={{ body: resumenMonedaColapsado ? { display: 'none' } : undefined }}
          extra={
            <Button
              type="text"
              size="small"
              icon={resumenMonedaColapsado ? <DownOutlined /> : <UpOutlined />}
              onClick={() => setResumenMonedaColapsado(!resumenMonedaColapsado)}
            >
              {resumenMonedaColapsado ? 'Maximizar' : 'Minimizar'}
            </Button>
          }
        >
          <Row gutter={[12, 12]}>
            {Object.entries(reporte.resumen_por_moneda).sort(([a], [b]) => a.localeCompare(b)).map(([moneda, rm]) => (
              <Col key={moneda} xs={24} sm={12} md={8} lg={6}>
                <Card size="small" style={{ background: '#fafafa' }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>{moneda}</Text>
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Text type="secondary">% Conciliado: <Text strong>{Math.round(rm.porcentaje_conciliado ?? 0)}%</Text></Text>
                    <Text type="secondary">Mov. Banco: <Text strong>{rm.total_movimientos_banco ?? 0}</Text></Text>
                    <Text type="secondary">PPD pagadas: <Text strong>{rm.ppd_pagadas ?? 0}</Text></Text>
                    <Text type="secondary">Total Banco: <Text strong>{formatMonto(rm.monto_total_banco, moneda)}</Text></Text>
                    <Text type="secondary">Total SAT: <Text strong>{formatMonto(rm.monto_total_sat_matched, moneda)}</Text></Text>
                    <Text type="secondary">Diferencia: <Text strong>{formatMonto(rm.diferencia, moneda)}</Text></Text>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {reporte.resumen_por_cuenta && Object.keys(reporte.resumen_por_cuenta).length > 1 && (
        <Card
          size="small"
          title="Resumen por cuenta"
          style={{ marginBottom: 20 }}
          styles={{ body: resumenCuentaColapsado ? { display: 'none' } : undefined }}
          extra={
            <Button
              type="text"
              size="small"
              icon={resumenCuentaColapsado ? <DownOutlined /> : <UpOutlined />}
              onClick={() => setResumenCuentaColapsado(!resumenCuentaColapsado)}
            >
              {resumenCuentaColapsado ? 'Maximizar' : 'Minimizar'}
            </Button>
          }
        >
          <Row gutter={[12, 12]}>
            {Object.entries(reporte.resumen_por_cuenta).sort(([a], [b]) => a.localeCompare(b)).map(([clave, rc]) => (
              <Col key={clave} xs={24} sm={12} md={8} lg={6}>
                <Card size="small" style={{ background: '#fafafa' }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>{rc.banco_fuente}{rc.numero_cuenta ? ` · ${rc.numero_cuenta}` : ''}</Text>
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Text type="secondary">Moneda: <Text strong>{rc.moneda}</Text></Text>
                    <Text type="secondary">Movimientos: <Text strong>{rc.total_movimientos ?? 0}</Text></Text>
                    <Text type="secondary">Depósitos: <Text strong>{formatMonto(rc.total_depositos, rc.moneda)}</Text></Text>
                    <Text type="secondary">Retiros: <Text strong>{formatMonto(rc.total_retiros, rc.moneda)}</Text></Text>
                    <Text type="secondary">Neto: <Text strong>{formatMonto(rc.neto, rc.moneda)}</Text></Text>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={key => {
          setActiveTab(key as 'resumen_cuenta' | 'sin_movimiento');
          resetFiltrosLocales();
        }}
        items={[
          { key: 'resumen_cuenta', label: `Resumen de Cuenta (${(r.matched ?? 0) + (r.matched_ia ?? 0) + (r.sin_cfdi ?? 0) + (r.conciliados_sin_cfdi ?? 0)})` },
          { key: 'sin_movimiento', label: `Sin movimiento (${(r.sin_movimiento_banco ?? 0)})` },
        ]}
        style={{ marginBottom: 4 }}
      />

      <Text style={{ fontSize: 12, display: 'block', textAlign: 'right', marginBottom: 16, color: 'rgba(0, 0, 0, 0.88)' }}>
        <Tag color="#198754" style={{ marginRight: 4 }}>MM</Tag>Match Manual &nbsp;·&nbsp;
        <Tag color="#198754" style={{ marginRight: 4 }}>TC</Tag>Traspaso entre Cuentas propias
        {CATEGORIAS_SIN_CFDI.map(c => (
          <span key={c.value}>
            &nbsp;·&nbsp;<Tag color="#198754" style={{ marginRight: 4 }}>{c.codigo}</Tag>{c.nombre}
          </span>
        ))}
      </Text>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 180 }}
            options={statusOptions}
          />
          <DatePicker.RangePicker
            value={rango ?? [null, null]}
            onChange={v => setRango(v ? [v[0], v[1]] : null)}
            format="YYYY-MM-DD"
            placeholder={['Desde', 'Hasta']}
            style={{ width: 260 }}
          />
          {activeTab === 'resumen_cuenta' && cuentas.length > 0 && (
            <Select
              value={filterCuenta}
              onChange={setFilterCuenta}
              style={{ width: 200 }}
              options={[
                { value: 'TODOS', label: 'Todas las Cuentas y Bancos' },
                ...cuentas
                  .filter(c => c.numero_cuenta)
                  .map(c => ({
                    value: c.numero_cuenta as string,
                    label: `${c.banco_fuente ?? '—'} — ${c.numero_cuenta}`,
                  })),
              ]}
            />
          )}
          {monedas.length > 1 && (
            <Select
              value={filterMoneda}
              onChange={setFilterMoneda}
              style={{ width: 150 }}
              options={[
                { value: 'TODOS', label: 'Todas las Monedas' },
                ...monedas.map(m => ({ value: m, label: m })),
              ]}
            />
          )}
          {tiposMetodos.length > 0 && (
            <Select
              value={filterTipoMetodo}
              onChange={setFilterTipoMetodo}
              style={{ width: 200 }}
              options={[
                { value: 'TODOS', label: 'Todos los Tipos' },
                ...tiposMetodos.map(tm => ({
                  value: tipoMetodoValue(tm.tipo_doc, tm.metodo_pago),
                  label: tipoMetodoLabel(tm.tipo_doc, tm.metodo_pago),
                })),
              ]}
            />
          )}
          <Input
            placeholder="Buscar por UUID"
            value={filterUuid}
            onChange={e => setFilterUuid(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="Buscar por RFC (emisor, receptor o banco)"
            value={filterRfc}
            onChange={e => setFilterRfc(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <InputNumber
            placeholder="Monto Mínimo"
            value={montoMin}
            onChange={v => setMontoMin(v)}
            style={{ width: 120 }}
            min={0}
          />
          <InputNumber
            placeholder="Monto Máximo"
            value={montoMax}
            onChange={v => setMontoMax(v)}
            style={{ width: 120 }}
            min={0}
          />
          {hayFiltros && (
            <Button type="link" onClick={resetFiltros}>
              Limpiar Filtros
            </Button>
          )}
          <Text type="secondary">{reporte.detalle.total} Registros</Text>
        </Space>
      </Card>

      <Table<DetalleItem>
        rowKey={(_, idx) => String(idx)}
        columns={columns}
        dataSource={displayItems}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total: reporte.detalle.total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        size="small"
        scroll={{ x: anchoTabla }}
        expandable={{
          rowExpandable: i => !!i.grupo_manual?.length,
          expandedRowRender: i => {
            const filasGrupo: DetalleItem[] = i.grupo_manual!.map(g => ({
              status: 'MATCHED',
              match_score: null,
              banco: {
                id: '', fecha: g.fecha, descripcion: null, deposito: null, retiro: null, monto: 0,
                banco_fuente: i.banco?.banco_fuente ?? null, numero_cuenta: i.banco?.numero_cuenta ?? null,
                orden: null, moneda: g.moneda,
              },
              cfdi: {
                uuid: g.uuid, rfc_emisor: g.rfc_emisor, rfc_receptor: g.rfc_receptor, total: g.total,
                fecha: g.fecha, forma_pago: null, metodo_pago: g.metodo_pago, tipo_doc: g.tipo_doc,
                moneda: g.moneda, ver_cfdi_url: null,
              },
              sugerencias: [],
            }));
            return (
              <div style={{ padding: '4px 0' }}>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  Match manual grupal — {filasGrupo.length} factura{filasGrupo.length !== 1 ? 's' : ''} más en este mismo movimiento:
                </Text>
                <Table<DetalleItem>
                  rowKey={row => row.cfdi!.uuid}
                  columns={columns}
                  dataSource={filasGrupo}
                  pagination={false}
                  showHeader={false}
                  size="small"
                  style={{ marginTop: 4 }}
                />
              </div>
            );
          },
        }}
        summary={() => {
          // antd cuenta la columna del ícono de expandir (índice 0) en los índices del summary.
          const colIndex = (key: string) => columns.findIndex(c => c.key === key) + 1;
          const iDeposito = colIndex('deposito');
          const iRetiro = colIndex('retiro');
          const iSaldo = colIndex('saldo');
          const iCfdiTotal = colIndex('cfdi_total');
          const totalCols = columns.length + 1;
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#f8f9fa', fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={iDeposito}>
                  <Text type="secondary">
                    TOTALES ({reporte.detalle.total} Registros){!monedaSaldo && monedasBanco.length > 1 && ' · multi-moneda'}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={iDeposito} align="right">
                  <Text style={{ color: '#198754' }}>
                    {monedaSaldo && totalesFiltrado.depositos > 0 ? formatMonto(totalesFiltrado.depositos, monedaSaldo) : '—'}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={iRetiro} align="right">
                  <Text style={{ color: '#dc3545' }}>
                    {monedaSaldo && totalesFiltrado.retiros > 0 ? formatMonto(totalesFiltrado.retiros, monedaSaldo) : '—'}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={iSaldo} align="right">
                  <Text strong>
                    {saldoVisible && monedaSaldo && totalesFiltrado.saldo_final != null ? formatMonto(totalesFiltrado.saldo_final, monedaSaldo) : '—'}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={iSaldo + 1} colSpan={iCfdiTotal - iSaldo - 1} />
                <Table.Summary.Cell index={iCfdiTotal} align="right">
                  <Text>
                    {monedaCfdiTotal && cfdiTotal != null ? formatMonto(cfdiTotal, monedaCfdiTotal) : '—'}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={iCfdiTotal + 1} colSpan={totalCols - iCfdiTotal - 1} />
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />

      <Modal
        title={anulandoTraspaso ? 'Deshacer traspaso entre cuentas' : anulandoSinCfdi ? `Deshacer ${anulandoSinCfdi}` : 'Anular match'}
        open={!!anularTarget}
        onCancel={() => { if (!anulando) { setAnularTarget(null); setMotivoAnular(''); } }}
        okText={anulandoTraspaso || anulandoSinCfdi ? 'Deshacer' : 'Anular'}
        okType="danger"
        okButtonProps={{ disabled: !motivoAnular.trim(), loading: anulando }}
        cancelButtonProps={{ disabled: anulando }}
        onOk={confirmarAnular}
      >
        {anulandoTraspaso ? (
          <Paragraph>
            Esta acción no se puede deshacer. El retiro y el depósito de este traspaso volverán a
            aparecer como "Sin CFDI" y estarán disponibles otra vez en Conciliación Manual.
          </Paragraph>
        ) : anulandoSinCfdi ? (
          <Paragraph>
            Esta acción no se puede deshacer. El movimiento dejará de estar conciliado como {anulandoSinCfdi.toLowerCase()},
            volverá a aparecer como "Sin CFDI" y estará disponible otra vez en Conciliación Manual.
          </Paragraph>
        ) : (
          <Paragraph>
            Esta acción no se puede deshacer. El movimiento bancario
            {anularTarget?.cfdi?.uuid ? ' y el CFDI' : ''} volverán a aparecer como "Sin CFDI"
            y estarán disponibles otra vez en Conciliación Manual.
          </Paragraph>
        )}
        <Paragraph strong style={{ marginBottom: 4 }}>Motivo (obligatorio)</Paragraph>
        <Input.TextArea
          rows={3}
          value={motivoAnular}
          onChange={e => setMotivoAnular(e.target.value)}
          placeholder={anulandoTraspaso
            ? 'Ej. no era un traspaso entre cuentas propias'
            : anulandoSinCfdi ? 'Ej. el movimiento sí tiene factura' : 'Ej. el CFDI no corresponde a este movimiento'}
          disabled={anulando}
        />
      </Modal>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: React.ReactNode;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Col xs={12} sm={8} md={6} lg={3} style={{ display: 'flex', flexDirection: 'column' }}>
      <Card size="small" style={{ flex: 1 }}>
        <Statistic title={label} value={value as string | number} valueStyle={{ color: '#1a3a5c', fontSize: 16 }} />
      </Card>
    </Col>
  );
}
